import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const WAITLIST_CLAIMED = 241;
const WAITLIST_TOTAL   = 500;

const FEATURES = [
  {
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
          d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
    title: 'Full analysis instantly',
    body: 'Cash flow, net operating income, cap rate, CoC return, BRRRR exit, and 10-year appreciation \u2014 pre-calculated on every listing the moment you open it.',
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
          d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    ),
    title: 'Rent benchmarks built in',
    body: 'Use HUD Fair Market Rent data as a baseline and adjust assumptions to match neighborhood-level market rents.',
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
          d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    title: 'BRRRR strategy calculator',
    body: 'Enter your rehab estimate and ARV. We instantly show your refi cash-out, infinite return potential, and remaining equity \u2014 live as you type.',
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
          d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
      </svg>
    ),
    title: 'Find top deals instantly',
    body: 'Set minimum cash flow, cap rate, or CoC return thresholds. Sort by profitability. Skip the spreadsheet hunting \u2014 only properties that hit your numbers show up.',
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
          d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
      </svg>
    ),
    title: 'Interactive map with filters',
    body: 'Visualize every deal on a live map. Filter by cash flow, cap rate, CoC return, price, property type, and listing status. Only deals that meet your criteria show up.',
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
          d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    title: 'Live assumption controls',
    body: 'Tweak down payment, interest rate, vacancy, management fee and more \u2014 every metric across every listing recalculates in real time.',
  },
];

const STEPS = [
  {
    n: '01',
    title: 'Enter your market',
    body: 'Type any ZIP code. We pull active listings and layer in rent benchmarks so underwriting starts instantly.',
  },
  {
    n: '02',
    title: 'Set your criteria',
    body: 'Filter by minimum cash flow, cap rate, price range, and property type. Only deals that pencil show up.',
  },
  {
    n: '03',
    title: 'Analyze in seconds',
    body: 'Cash flow, BRRRR, appreciation \u2014 every number is waiting for you before you pick up the phone.',
  },
  {
    n: '04',
    title: 'Make confident offers',
    body: 'Go into negotiations knowing your exact numbers. No more guessing, no more spreadsheets.',
  },
];

const PRICING_ITEMS = [
  'Live MLS listings \u2014 Greater Cleveland metro area',
  'Rent benchmarks (including HUD FMR) for 6 counties',
  'Cash flow, cap rate and BRRRR calculator',
  'Find top deals instantly \u2014 filter by cash flow, cap rate & CoC return',
  'Interactive map with pin clusters',
  'Appreciation projections \u2014 5, 10, and 30-year equity buildup',
  'Property tax & insurance estimates',
  'Live assumption controls \u2014 every metric updates instantly',
  'Unlimited property analyses',
];

/* ------------------------------------------------------------------ */
/*  Injected CSS for keyframe animations                               */
/* ------------------------------------------------------------------ */
const luxuryCSS = `
@keyframes shimmer {
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
}
@keyframes shimmerSlow {
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
}
@keyframes float {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-8px); }
}
@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(24px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes pulseGlow {
  0%, 100% { box-shadow: 0 0 20px rgba(16,185,129,0.15), 0 0 60px rgba(16,185,129,0.05); }
  50% { box-shadow: 0 0 30px rgba(16,185,129,0.25), 0 0 80px rgba(16,185,129,0.1); }
}
@keyframes sparkle {
  0%, 100% { opacity: 0.3; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.2); }
}
@keyframes borderRotate {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
@keyframes colorShift {
  0%, 100% { filter: hue-rotate(0deg); }
  50% { filter: hue-rotate(30deg); }
}
.animate-shimmer {
  background-size: 200% auto;
  animation: shimmer 3s linear infinite;
}
.animate-shimmer-slow {
  background-size: 200% auto;
  animation: shimmerSlow 6s linear infinite;
}
.animate-float {
  animation: float 6s ease-in-out infinite;
}
.animate-float-delayed {
  animation: float 6s ease-in-out 2s infinite;
}
.animate-float-slow {
  animation: float 8s ease-in-out 1s infinite;
}
.animate-fadeInUp {
  animation: fadeInUp 0.8s ease-out both;
}
.animate-pulseGlow {
  animation: pulseGlow 3s ease-in-out infinite;
}
.animate-sparkle {
  animation: sparkle 2s ease-in-out infinite;
}
.animate-sparkle-delayed {
  animation: sparkle 2s ease-in-out 0.7s infinite;
}
.animate-sparkle-slow {
  animation: sparkle 3s ease-in-out 1.4s infinite;
}
.email-glow-wrapper {
  position: relative;
  border-radius: 0.75rem;
  overflow: hidden;
  padding: 2px;
}
.email-glow-wrapper::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: conic-gradient(
    from 0deg,
    transparent 0deg,
    #10b981 40deg,
    #14b8a6 80deg,
    #06b6d4 120deg,
    transparent 160deg,
    transparent 200deg,
    #d4a04a 240deg,
    #f59e0b 280deg,
    transparent 320deg,
    transparent 360deg
  );
  animation: borderRotate 3s linear infinite, colorShift 6s ease-in-out infinite;
  z-index: 0;
}
.email-glow-wrapper::after {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: conic-gradient(
    from 180deg,
    transparent 0deg,
    rgba(16,185,129,0.4) 30deg,
    transparent 60deg,
    transparent 180deg,
    rgba(245,158,11,0.3) 210deg,
    transparent 240deg
  );
  animation: borderRotate 2s linear infinite reverse;
  z-index: 0;
}
.email-glow-input {
  position: relative;
  z-index: 1;
  border-radius: calc(0.75rem - 2px);
  width: 100%;
}
`;

/* ------------------------------------------------------------------ */
/*  Grain overlay                                                      */
/* ------------------------------------------------------------------ */
function GrainOverlay() {
  return (
    <svg className="pointer-events-none fixed inset-0 z-[1] w-full h-full opacity-[0.025]" aria-hidden="true">
      <filter id="grain">
        <feTurbulence type="fractalNoise" baseFrequency="0.65" numOctaves="3" stitchTiles="stitch" />
        <feColorMatrix type="saturate" values="0" />
      </filter>
      <rect width="100%" height="100%" filter="url(#grain)" />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/*  Decorative sparkle diamond                                         */
/* ------------------------------------------------------------------ */
function Sparkle({ className = '' }: { className?: string }) {
  return (
    <svg className={className} width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M8 0L9.5 6.5L16 8L9.5 9.5L8 16L6.5 9.5L0 8L6.5 6.5L8 0Z" fill="currentColor" />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/*  Curvy wave SVG dividers                                            */
/* ------------------------------------------------------------------ */
function WaveTop({ color, className = '' }: { color: string; className?: string }) {
  return (
    <svg className={`w-full block ${className}`} viewBox="0 0 1440 100" preserveAspectRatio="none" style={{ height: '60px' }}>
      <path d="M0,80 C240,0 480,100 720,50 C960,0 1200,80 1440,20 L1440,100 L0,100 Z" fill={color} />
    </svg>
  );
}

function WaveBottom({ color, className = '' }: { color: string; className?: string }) {
  return (
    <svg className={`w-full block ${className}`} viewBox="0 0 1440 100" preserveAspectRatio="none" style={{ height: '60px' }}>
      <path d="M0,20 C240,100 480,0 720,50 C960,100 1200,20 1440,80 L1440,0 L0,0 Z" fill={color} />
    </svg>
  );
}

function WaveSoft({ color, flip = false, className = '' }: { color: string; flip?: boolean; className?: string }) {
  return (
    <svg className={`w-full block ${flip ? 'rotate-180' : ''} ${className}`} viewBox="0 0 1440 80" preserveAspectRatio="none" style={{ height: '50px' }}>
      <path d="M0,60 C360,10 720,90 1080,30 C1260,10 1380,40 1440,25 L1440,80 L0,80 Z" fill={color} />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/*  Inline SVG mockup illustrations (no external images needed)        */
/* ------------------------------------------------------------------ */
function MockupMap() {
  return (
    <div className="w-full aspect-square bg-gradient-to-br from-stone-200 via-stone-100 to-stone-50 relative overflow-hidden">
      {/* Map grid lines - street grid */}
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 300">
        {/* Vertical streets */}
        {[60,120,180,240,300,360].map(x => <line key={`v${x}`} x1={x} y1="0" x2={x} y2="300" stroke="#cbd5e1" strokeWidth="2.5" opacity="0.6" />)}
        {/* Horizontal streets */}
        {[50,100,150,200,250].map(y => <line key={`h${y}`} x1="0" y1={y} x2="400" y2={y} stroke="#cbd5e1" strokeWidth="2.5" opacity="0.6" />)}
        {/* Street center lines */}
        {[60,120,180,240,300,360].map(x => <line key={`vc${x}`} x1={x} y1="0" x2={x} y2="300" stroke="#fbbf24" strokeWidth="0.5" opacity="0.4" strokeDasharray="4,4" />)}
        {[50,100,150,200,250].map(y => <line key={`hc${y}`} x1="0" y1={y} x2="400" y2={y} stroke="#fbbf24" strokeWidth="0.5" opacity="0.4" strokeDasharray="4,4" />)}
      </svg>
      {/* City blocks */}
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 300">
        {/* Park areas */}
        <rect x="125" y="55" width="50" height="40" fill="#86efac" opacity="0.3" />
        <rect x="245" y="155" width="50" height="40" fill="#86efac" opacity="0.3" />
        {/* Building blocks */}
        <rect x="65" y="55" width="15" height="12" fill="#94a3b8" opacity="0.2" />
        <rect x="190" y="105" width="18" height="14" fill="#94a3b8" opacity="0.2" />
        <rect x="310" y="80" width="12" height="10" fill="#94a3b8" opacity="0.2" />
      </svg>
      {/* Property markers with cash-flow-based colors */}
      {[
        { x: 80, y: 70, price: '$45K', color: '#047857' },
        { x: 200, y: 105, price: '$72K', color: '#10b981' },
        { x: 300, y: 80, price: '$58K', color: '#6ee7b7' },
        { x: 140, y: 180, price: '$95K', color: '#f87171' },
        { x: 260, y: 190, price: '$38K', color: '#047857' },
        { x: 340, y: 160, price: '$67K', color: '#dc2626' },
        { x: 100, y: 240, price: '$52K', color: '#059669' },
        { x: 220, y: 250, price: '$81K', color: '#34d399' },
      ].map((m, i) => (
        <div key={i} className="absolute" style={{ left: `${(m.x / 400) * 100}%`, top: `${(m.y / 300) * 100}%`, transform: 'translate(-50%, -100%)' }}>
          <div className="relative">
            <div className="px-3 py-1.5 rounded-lg text-[14px] font-black text-white shadow-xl border-2 border-white" style={{ backgroundColor: m.color }}>
              {m.price}
            </div>
            <div className="w-0 h-0 mx-auto" style={{ borderLeft: '7px solid transparent', borderRight: '7px solid transparent', borderTop: `9px solid ${m.color}` }} />
          </div>
        </div>
      ))}
      {/* Legend */}
      <div className="absolute bottom-3 left-3 bg-white/95 backdrop-blur-sm rounded-lg shadow-lg px-3 py-2 flex flex-col gap-1.5 border border-stone-200">
        <div className="text-[11px] font-bold text-stone-700 uppercase tracking-wider mb-0.5">Cash Flow</div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-sm shadow-sm" style={{ backgroundColor: '#047857' }} />
          <span className="text-[11px] text-stone-600 font-medium">High (&gt;$400)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-sm shadow-sm" style={{ backgroundColor: '#10b981' }} />
          <span className="text-[11px] text-stone-600 font-medium">Good ($200-$400)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-sm shadow-sm" style={{ backgroundColor: '#6ee7b7' }} />
          <span className="text-[11px] text-stone-600 font-medium">Low ($0-$200)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-sm shadow-sm" style={{ backgroundColor: '#dc2626' }} />
          <span className="text-[11px] text-stone-600 font-medium">Negative</span>
        </div>
      </div>
      {/* Zoom controls */}
      <div className="absolute top-4 right-4 flex flex-col gap-1">
        <div className="w-9 h-9 bg-white rounded-lg shadow-md border border-stone-200 text-stone-600 flex items-center justify-center text-lg font-bold hover:bg-stone-50 cursor-pointer">+</div>
        <div className="w-9 h-9 bg-white rounded-lg shadow-md border border-stone-200 text-stone-600 flex items-center justify-center text-lg font-bold hover:bg-stone-50 cursor-pointer">&minus;</div>
      </div>
    </div>
  );
}

function MockupCashFlow() {
  return (
    <div className="w-full aspect-square bg-white relative overflow-hidden">
      {/* Income vs Expenses Bar */}
      <div className="px-3 py-2 bg-stone-50">
        <div className="flex justify-between items-center mb-1">
          <span className="text-[11px] text-stone-500 uppercase tracking-wide font-semibold">Income vs Expenses</span>
          <span className="text-[13px] font-bold text-emerald-600">+$1,510/MO NET</span>
        </div>
        <div className="h-3 flex rounded-sm overflow-hidden mb-1">
          <div className="bg-red-400" style={{ width: '61%' }} />
          <div className="bg-emerald-500" style={{ width: '39%' }} />
        </div>
        <div className="flex justify-between text-[10px] text-stone-400">
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 bg-red-400 rounded-sm" />
            <span>Expenses 61%</span>
            <div className="w-2.5 h-2.5 bg-emerald-500 rounded-sm ml-1" />
            <span>Cash Flow 39%</span>
          </div>
          <span>Rent: $3,920/mo</span>
        </div>
      </div>

      {/* Monthly Income */}
      <div className="px-2 py-0.5">
        <div className="bg-emerald-600 px-1.5 py-0.5 flex items-center justify-between mb-0.5">
          <div className="flex items-center gap-0.5">
            <div className="w-2 h-2 bg-white rounded-sm" />
            <span className="text-white text-[11px] font-bold uppercase tracking-wide">Monthly Income</span>
          </div>
          <span className="text-white text-[14px] font-bold">$3,920/mo</span>
        </div>
        <div className="text-[10px] mb-1">
          <div className="flex justify-between text-stone-600">
            <span>Unit 1 — 2BR/1BA</span>
            <span className="font-medium">$1,070/mo</span>
          </div>
          <div className="flex justify-between text-stone-600">
            <span>Unit 2 — 2BR/1BA</span>
            <span className="font-medium">$1,070/mo</span>
          </div>
          <div className="flex justify-between text-stone-600">
            <span>Unit 3 — 1BR/1BA</span>
            <span className="font-medium">$890/mo</span>
          </div>
          <div className="flex justify-between text-stone-600">
            <span>Unit 4 — 1BR/1BA</span>
            <span className="font-medium">$890/mo</span>
          </div>
          <div className="flex justify-between text-stone-700 font-semibold pt-0.5 border-t border-stone-200 mt-0.5">
            <span>Total Estimated Rent</span>
            <span>$3,920/mo</span>
          </div>
        </div>
      </div>

      {/* Monthly Expenses */}
      <div className="px-2 py-0.5">
        <div className="bg-red-500 px-1.5 py-0.5 flex items-center justify-between mb-0.5">
          <div className="flex items-center gap-0.5">
            <div className="w-2 h-2 bg-white rounded-sm" />
            <span className="text-white text-[11px] font-bold uppercase tracking-wide">Monthly Expenses</span>
          </div>
          <span className="text-white text-[14px] font-bold">-$2,410/mo</span>
        </div>
        <div className="text-[10px] mb-1">
          <div className="flex justify-between text-stone-600">
            <span>Principal + Interest (Mortgage)</span>
            <span className="font-medium">-$1,059</span>
          </div>
          <div className="flex justify-between text-stone-600">
            <span>Property Tax (1.30% actual)</span>
            <span className="font-medium">-$100</span>
          </div>
          <div className="flex justify-between text-stone-600">
            <span>Homeowners Insurance</span>
            <span className="font-medium">-$77</span>
          </div>
          <div className="flex justify-between text-stone-600">
            <span>Maintenance</span>
            <span className="font-medium">-$77</span>
          </div>
          <div className="flex justify-between text-stone-600">
            <span>Property Management (10%)</span>
            <span className="font-medium">-$392</span>
          </div>
          <div className="flex justify-between text-stone-600">
            <span>CapEx Reserve (10%)</span>
            <span className="font-medium">-$392</span>
          </div>
          <div className="flex justify-between text-stone-600">
            <span>Vacancy Allowance (8%)</span>
            <span className="font-medium">-$314</span>
          </div>
          <div className="flex justify-between text-stone-700 font-semibold pt-0.5 border-t border-stone-200 mt-0.5">
            <span>Total Expenses</span>
            <span>-$2,410/mo</span>
          </div>
        </div>
      </div>

      {/* Monthly Cash Flow */}
      <div className="mx-3 mb-2 bg-emerald-50 border-2 border-emerald-200 rounded-lg px-3 py-2 text-center">
        <div className="text-[10px] text-stone-500 uppercase tracking-wide font-bold">Monthly Cash Flow</div>
        <div className="text-[18px] font-black text-emerald-600">+$1,510/mo</div>
        <div className="text-[9px] text-stone-400">$18,123/yr · 61% of rent goes to expenses</div>
      </div>
    </div>
  );
}

function MockupBRRRR() {
  return (
    <div className="w-full aspect-square bg-white relative overflow-hidden">
      {/* Your Estimates */}
      <div className="px-4 py-1 bg-amber-50/50">
        <div className="text-[10px] text-amber-800 font-bold uppercase mb-1">Your Estimates</div>
        <div className="grid grid-cols-3 gap-1.5 text-[10px]">
          <div>
            <div className="text-stone-500">🔨 Rehab Cost</div>
            <div className="font-bold text-stone-700 text-[11px]">$20000</div>
          </div>
          <div>
            <div className="text-stone-500">🏠 ARV</div>
            <div className="font-bold text-stone-700 text-[11px]">$300000</div>
          </div>
          <div>
            <div className="text-stone-500">💰 Rent After</div>
            <div className="font-bold text-stone-700 text-[11px]">$4200</div>
          </div>
        </div>
      </div>

      {/* Buy */}
      <div className="px-4 py-1 bg-blue-50">
        <div className="flex items-center gap-0.5 mb-0.5">
          <span className="text-[11px] text-blue-700 font-bold">🏡 Buy</span>
        </div>
        <div className="text-[10px]">
          <div className="flex justify-between text-stone-600">
            <span>Purchase price</span>
            <span className="font-medium">$199,000</span>
          </div>
          <div className="flex justify-between text-stone-600">
            <span>− Down payment (20%)</span>
            <span className="font-medium">−$39,800</span>
          </div>
          <div className="flex justify-between text-stone-700 font-bold border-t border-blue-200 mt-0.5 pt-0.5">
            <span>= Original loan</span>
            <span>$159,200</span>
          </div>
        </div>
      </div>

      {/* Rehab */}
      <div className="px-4 py-1 bg-orange-50">
        <div className="flex items-center gap-0.5 mb-0.5">
          <span className="text-[11px] text-amber-700 font-bold">🔧 Rehab</span>
        </div>
        <div className="text-[10px]">
          <div className="flex justify-between text-stone-600">
            <span>Purchase price</span>
            <span className="font-medium">$199,000</span>
          </div>
          <div className="flex justify-between text-stone-600">
            <span>+ Rehab / improvements</span>
            <span className="font-medium">$20,000</span>
          </div>
          <div className="flex justify-between text-stone-700 font-bold border-t border-orange-200 mt-0.5 pt-0.5">
            <span>= Total investment</span>
            <span className="text-blue-600">$219,000</span>
          </div>
        </div>
      </div>

      {/* ARV */}
      <div className="px-4 py-1 bg-green-50">
        <div className="flex items-center gap-0.5 mb-0.5">
          <span className="text-[11px] text-emerald-700 font-bold">✅ ARV</span>
        </div>
        <div className="text-[10px]">
          <div className="flex justify-between text-stone-600">
            <span>ARV estimate</span>
            <span className="font-medium">$300,000</span>
          </div>
          <div className="flex justify-between text-stone-600">
            <span>− Total investment</span>
            <span className="font-medium">−$219,000</span>
          </div>
          <div className="flex justify-between text-emerald-700 font-bold border-t border-emerald-200 mt-0.5 pt-0.5">
            <span>= Instant equity</span>
            <span>$81,000</span>
          </div>
        </div>
      </div>

      {/* Refinance */}
      <div className="px-4 py-1 bg-purple-50">
        <div className="flex items-center gap-0.5 mb-0.5">
          <span className="text-[11px] text-purple-700 font-bold">🏦 Refinance</span>
        </div>
        <div className="text-[10px]">
          <div className="flex justify-between text-stone-600">
            <span>ARV × 75% LTV</span>
            <span className="font-medium">$225,000</span>
          </div>
          <div className="flex justify-between text-stone-600">
            <span>− Original loan payoff</span>
            <span className="font-medium">−$159,200</span>
          </div>
          <div className="flex justify-between text-stone-600">
            <span>− Refi closing costs</span>
            <span className="font-medium">−$6,750</span>
          </div>
          <div className="flex justify-between text-stone-600">
            <span>= Gross refi proceeds</span>
            <span className="font-medium">$65,800</span>
          </div>
        </div>
      </div>

      {/* Outcome */}
      <div className="mx-4 my-1.5 bg-emerald-50 border-2 border-emerald-300 rounded-lg px-4 py-1.5">
        <div className="text-[10px] text-stone-500 uppercase mb-1 font-bold">Outcome</div>
        <div className="grid grid-cols-6 gap-1.5 text-[10px]">
          <div className="text-center">
            <div className="text-[9px] text-stone-500">Cash Out</div>
            <div className="font-bold text-emerald-600 text-[11px]">$59,050</div>
            <div className="text-[8px] text-amber-600">🔥 best</div>
          </div>
          <div className="text-center">
            <div className="text-[9px] text-stone-500">New Mortgage</div>
            <div className="font-bold text-stone-700 text-[11px]">$225,000</div>
            <div className="text-[8px] text-stone-400">75% LTV</div>
          </div>
          <div className="text-center">
            <div className="text-[9px] text-stone-500">Equity Left</div>
            <div className="font-bold text-stone-700 text-[11px]">$75,000</div>
            <div className="text-[8px] text-stone-400">25% loan</div>
          </div>
          <div className="text-center">
            <div className="text-[9px] text-stone-500">Equity Gain</div>
            <div className="font-bold text-emerald-600 text-[11px]">+$35,200</div>
            <div className="text-[8px] text-emerald-600">✅ created</div>
          </div>
          <div className="text-center">
            <div className="text-[9px] text-stone-500">Cash Flow</div>
            <div className="font-bold text-emerald-600 text-[11px]">$360/mo</div>
            <div className="text-[8px] text-stone-400">$4,416/yr</div>
          </div>
          <div className="text-center">
            <div className="text-[9px] text-stone-500">CoC Return</div>
            <div className="font-bold text-amber-600 text-[11px]">24.4%</div>
            <div className="text-[8px] text-amber-600">🔥 Excellent</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MockupPropertyDetail() {
  const properties = [
    {
      address: '5806 Fleet Ave',
      city: 'Cleveland, OH 44105',
      type: 'Multi Family',
      price: '$114,900',
      asking: '$114,900',
      beds: 7,
      baths: 4,
      sqft: 0,
      cashFlow: '$2,061/mo',
      cashFlowYr: '$24,735/yr',
      capRate: '27.91%',
      grossRent: '$4,230',
      noi: '$32,074',
      coc: '70.82%',
      payback: '14 yrs',
      marketValue: '$97,040',
      equity: '-$17,860',
      equityPct: '-15.5% vs price',
      maxBuy: '$338,906',
      maxBuyUnder: '$224,006 under',
      imageUrl: 'https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=400&h=300&fit=crop'
    },
    {
      address: '3421 E 147th St',
      city: 'Cleveland, OH 44120',
      type: 'Single Family',
      price: '$89,000',
      asking: '$89,000',
      beds: 3,
      baths: 2,
      sqft: 1200,
      cashFlow: '$1,245/mo',
      cashFlowYr: '$14,940/yr',
      capRate: '18.5%',
      grossRent: '$1,850',
      noi: '$16,450',
      coc: '45.2%',
      payback: '22 yrs',
      marketValue: '$92,000',
      equity: '$3,000',
      equityPct: '3.4% vs price',
      maxBuy: '$125,500',
      maxBuyUnder: '$36,500 under',
      imageUrl: 'https://images.unsplash.com/photo-1570129477492-45c003edd2be?w=400&h=300&fit=crop'
    },
    {
      address: '12908 Union Ave',
      city: 'Cleveland, OH 44105',
      type: 'Multi Family',
      price: '$145,000',
      asking: '$145,000',
      beds: 8,
      baths: 4,
      sqft: 0,
      cashFlow: '$2,890/mo',
      cashFlowYr: '$34,680/yr',
      capRate: '24.3%',
      grossRent: '$5,100',
      noi: '$35,230',
      coc: '62.1%',
      payback: '16 yrs',
      marketValue: '$138,000',
      equity: '-$7,000',
      equityPct: '-4.8% vs price',
      maxBuy: '$285,900',
      maxBuyUnder: '$140,900 under',
      imageUrl: 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=400&h=300&fit=crop'
    }
  ];

  return (
    <div className="w-full aspect-square bg-stone-50 relative overflow-hidden">
      {/* Scrollable Property List */}
      <div className="overflow-y-auto h-full">
        <div className="p-1.5 space-y-1.5">
          {properties.map((property, idx) => (
            <div key={idx} className="bg-white rounded border border-stone-200 overflow-hidden">
              <div className="flex">
                {/* Left: Property Image */}
                <div className="w-[35%] relative flex-shrink-0">
                  <img 
                    src={property.imageUrl} 
                    alt={property.address}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute bottom-1.5 left-1.5 right-1.5 space-y-0.5">
                    <button className="w-full bg-white/95 backdrop-blur-sm text-blue-600 text-[7px] py-1 rounded flex items-center justify-center gap-1">
                      <span>📋</span> View Full Listing on Zillow
                    </button>
                    <button className="w-full bg-white/95 backdrop-blur-sm text-blue-600 text-[7px] py-1 rounded flex items-center justify-center gap-1">
                      <span>📍</span> View on Google Maps
                    </button>
                  </div>
                </div>

                {/* Right: Property Details */}
                <div className="flex-1 p-1.5">
                  {/* Header */}
                  <div className="mb-1.5 relative">
                    <div className="text-[11px] font-bold text-stone-900">{property.address}</div>
                    <div className="text-[7px] text-stone-500">{property.city} • {property.type}</div>
                    <div className="absolute top-0 right-0 bg-emerald-500 text-white px-1.5 py-0.5 rounded text-[8px] font-bold">
                      {property.cashFlow}
                    </div>
                    <div className="mt-0.5 flex items-baseline gap-1">
                      <div className="text-[13px] font-black text-stone-900">{property.price}</div>
                    </div>
                    <div className="text-[7px] text-stone-500">{property.beds} bd / {property.baths} ba{property.sqft > 0 ? ` • ${property.sqft} sqft` : ''} • Built Unknown</div>
                    <div className="text-[6px] text-stone-400 italic mt-0.5">* Assumes full occupancy using current rent assumptions. Adjust renovations and rent assumptions as needed.</div>
                  </div>

                  {/* Metrics Grid - 2 rows */}
                  <div className="grid grid-cols-4 gap-0.5 mb-1">
                    {/* Row 1 */}
                    <div className="bg-emerald-500 text-white rounded px-1 py-0.5">
                      <div className="text-[5px] opacity-90">GROSS RENT/MO</div>
                      <div className="text-[9px] font-bold">{property.grossRent}</div>
                      <div className="text-[5px]">🔥 Excellent</div>
                    </div>
                    
                    <div className="bg-emerald-500 text-white rounded px-1 py-0.5">
                      <div className="text-[5px] opacity-90">CASH FLOW</div>
                      <div className="text-[9px] font-bold">{property.cashFlow}</div>
                      <div className="text-[5px]">{property.cashFlowYr}</div>
                    </div>
                    
                    <div className="bg-teal-500 text-white rounded px-1 py-0.5">
                      <div className="text-[5px] opacity-90">NOI (ANNUAL)</div>
                      <div className="text-[9px] font-bold">{property.noi}</div>
                    </div>
                    
                    <div className="bg-indigo-500 text-white rounded px-1 py-0.5">
                      <div className="text-[5px] opacity-90">CAP RATE</div>
                      <div className="text-[9px] font-bold">{property.capRate}</div>
                      <div className="text-[5px]">🔥 Excellent</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-4 gap-0.5 mb-1">
                    {/* Row 2 */}
                    <div className="bg-purple-600 text-white rounded px-1 py-0.5">
                      <div className="text-[5px] opacity-90">CASH-ON-CASH</div>
                      <div className="text-[9px] font-bold">{property.coc}</div>
                      <div className="text-[5px]">Payback: {property.payback}</div>
                    </div>
                    
                    <div className="bg-purple-500 text-white rounded px-1 py-0.5">
                      <div className="text-[5px] opacity-90">MARKET VALUE EST.</div>
                      <div className="text-[9px] font-bold">{property.marketValue}</div>
                      <div className="text-[5px]">Avg of 2-3 models</div>
                    </div>
                    
                    <div className="bg-orange-600 text-white rounded px-1 py-0.5">
                      <div className="text-[5px] opacity-90">EQUITY AT PURCHASE</div>
                      <div className="text-[9px] font-bold">{property.equity}</div>
                      <div className="text-[5px]">{property.equityPct}</div>
                    </div>
                    
                    <div className="bg-teal-600 text-white rounded px-1 py-0.5">
                      <div className="text-[5px] opacity-90">MAX BUY PRICE</div>
                      <div className="text-[9px] font-bold">{property.maxBuy}</div>
                      <div className="text-[5px]">✅ {property.maxBuyUnder}</div>
                    </div>
                  </div>

                  {/* Collapsible Sections */}
                  <div className="space-y-0.5">
                    <div className="flex items-center justify-between py-0.5 bg-green-50 px-1 rounded">
                      <div className="flex items-center gap-1">
                        <span className="text-[7px]">💰</span>
                        <div>
                          <div className="text-[7px] font-bold text-stone-900">Cash Flow Analysis</div>
                          <div className="text-[5px] text-stone-500">{property.grossRent} rent • {property.cashFlow} net</div>
                        </div>
                      </div>
                      <span className="text-[6px] text-stone-400">▼</span>
                    </div>
                    
                    <div className="flex items-center justify-between py-0.5 bg-blue-50 px-1 rounded">
                      <div className="flex items-center gap-1">
                        <span className="text-[7px]">🔄</span>
                        <div>
                          <div className="text-[7px] font-bold text-stone-900">BRRRR Strategy</div>
                          <div className="text-[5px] text-stone-500">$22,640 pulled out • 45.6% return</div>
                        </div>
                      </div>
                      <span className="text-[6px] text-stone-400">▼</span>
                    </div>
                    
                    <div className="flex items-center justify-between py-0.5 bg-purple-50 px-1 rounded">
                      <div className="flex items-center gap-1">
                        <span className="text-[7px]">📈</span>
                        <div>
                          <div className="text-[7px] font-bold text-stone-900">Appreciation & Growth</div>
                          <div className="text-[5px] text-stone-500">2.0% annual • $268,601 total in 10yr</div>
                        </div>
                      </div>
                      <span className="text-[6px] text-stone-400">▼</span>
                    </div>

                    <div className="flex items-center justify-between py-0.5 bg-amber-50 px-1 rounded">
                      <div className="flex items-center gap-1">
                        <span className="text-[7px]">📊</span>
                        <div>
                          <div className="text-[7px] font-bold text-stone-900">Property Value Estimates</div>
                          <div className="text-[5px] text-stone-500">1 estimates • Avg: {property.marketValue}</div>
                        </div>
                      </div>
                      <span className="text-[6px] text-stone-400">▼</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* Screenshot card data (now using mockup components) */
const SCREENSHOTS = [
  {
    Mockup: MockupPropertyDetail,
    title: 'Property Detail View',
    desc: 'Complete property breakdown with 8 key metrics at a glance. Cap rate, cash-on-cash return, market value, equity position, and max buy price — all color-coded for instant insight.',
  },
  {
    Mockup: MockupCashFlow,
    title: 'Multi-Unit Cash Flow',
    desc: 'See per-unit income and expenses for multi-family properties. Rent, mortgage, taxes, insurance, and vacancy are broken down unit by unit.',
  },
  {
    Mockup: MockupMap,
    title: 'Interactive Deal Map',
    desc: 'Color-coded by cash flow — dark green means high profit, red means negative. Filter by price, cap rate, status, and property type.',
  },
  {
    Mockup: MockupBRRRR,
    title: 'BRRRR Strategy Calculator',
    desc: 'Full Buy-Rehab-Rent-Refinance-Repeat analysis. See your cash back at refi, equity created, new monthly payment, and cash-on-cash return.',
  },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const { isAuthenticated, appMode, referralsEnabled } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const isWaitlistOrBeta = appMode !== 'production';

  async function handleWaitlistSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError('');
    setLoading(true);
    try {
      await api.post('/api/v1/waitlist/join', { email });
      setDone(true);
    } catch (err: any) {
      setSubmitError(err?.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  /* ---------- Email input with rotating glow ---------- */
  function GlowEmailInput() {
    return (
      <div className="email-glow-wrapper flex-1">
        <input
          type="email"
          required
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="Enter your email address"
          className="email-glow-input bg-white/95 backdrop-blur-md text-stone-900 placeholder-stone-400 px-5 py-4 text-sm focus:outline-none transition-all"
        />
      </div>
    );
  }

  /* ---------- Dark-themed email input for CTA ---------- */
  function GlowEmailInputDark() {
    return (
      <div className="email-glow-wrapper flex-1">
        <input
          type="email"
          required
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="Enter your email address"
          className="email-glow-input bg-white/10 backdrop-blur-md text-white placeholder-stone-400 px-5 py-4 text-sm focus:outline-none transition-all"
        />
      </div>
    );
  }

  /* ---------- Success screen ---------- */
  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: 'linear-gradient(135deg, #a7f3d0 0%, #99f6e4 20%, #ecfdf5 40%, #fef9c3 70%, #fbcfe8 100%)' }}>
        <style>{luxuryCSS}</style>
        <GrainOverlay />
        <div className="relative z-10 text-center max-w-md animate-fadeInUp">
          <div className="mx-auto mb-8 w-20 h-20 rounded-full bg-gradient-to-br from-emerald-600 to-teal-600 flex items-center justify-center shadow-2xl shadow-emerald-900/25 animate-pulseGlow">
            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-3xl font-bold text-stone-900 mb-3">You&apos;re on the list.</h2>
          <p className="text-stone-500 mb-8 leading-relaxed">
            We&apos;ll reach out the moment your spot opens. Early members get locked-in pricing &mdash; you
            won&apos;t pay more than what&apos;s reserved for you today.
          </p>
          <button
            onClick={() => setDone(false)}
            className="inline-flex items-center gap-2 text-emerald-600 text-sm font-semibold hover:text-emerald-700 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to home
          </button>
        </div>
      </div>
    );
  }

  /* ---------- Main page ---------- */
  return (
    <div className="min-h-screen text-stone-900 overflow-x-hidden font-sans bg-white">
      <style>{luxuryCSS}</style>
      <GrainOverlay />

      {/* ============================================================ */}
      {/*  NAV                                                         */}
      {/* ============================================================ */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl" style={{ background: 'rgba(255,255,255,0.85)' }}>
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-600 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-900/20 group-hover:shadow-emerald-900/30 transition-shadow">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </div>
            <span className="font-bold text-stone-900 text-base tracking-tight">FindBestRentals</span>
          </Link>
          <div className="flex items-center gap-3">
            {isAuthenticated ? (
              <button
                onClick={() => navigate('/app')}
                className="text-sm font-bold bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white px-5 py-2 rounded-lg transition-all shadow-lg shadow-emerald-900/20 hover:-translate-y-px"
              >
                Go to App
              </button>
            ) : (
              <>
                <button
                  onClick={() => navigate('/login')}
                  className="text-sm font-medium text-stone-500 hover:text-stone-900 transition-colors px-4 py-2"
                >
                  Log In
                </button>
                {isWaitlistOrBeta ? (
                  <button
                    onClick={() => document.getElementById('hero-form')?.scrollIntoView({ behavior: 'smooth' })}
                    className="text-sm font-bold bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white px-5 py-2 rounded-lg transition-all shadow-lg shadow-emerald-900/20 hover:-translate-y-px"
                  >
                    Join Waitlist
                  </button>
                ) : (
                  <button
                    onClick={() => navigate('/signup')}
                    className="text-sm font-bold bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white px-5 py-2 rounded-lg transition-all shadow-lg shadow-emerald-900/20 hover:-translate-y-px"
                  >
                    Start Free Trial
                  </button>
                )}
              </>
            )}
          </div>
        </div>
        <div className="h-px" style={{ background: 'linear-gradient(90deg, transparent 5%, rgba(16,185,129,0.15) 25%, rgba(217,178,90,0.2) 50%, rgba(20,184,166,0.15) 75%, transparent 95%)' }} />
      </nav>

      {/* ============================================================ */}
      {/*  HERO - vibrant, colorful gradient with bold organic shapes  */}
      {/* ============================================================ */}
      <section className="relative pt-36 pb-20 px-6 overflow-hidden" style={{ background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 15%, #99f6e4 30%, #cffafe 45%, #ecfdf5 55%, #fef9c3 70%, #fde68a 85%, #fbcfe8 100%)' }}>
        {/* Large organic blob shapes - more saturated */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
          <div className="absolute top-[-20%] left-[-10%] w-[800px] h-[800px] animate-float" style={{ background: 'radial-gradient(circle, rgba(16,185,129,0.25) 0%, rgba(6,182,212,0.1) 40%, transparent 65%)', borderRadius: '40% 60% 65% 35% / 55% 40% 60% 45%' }} />
          <div className="absolute top-[-5%] right-[-10%] w-[600px] h-[600px] animate-float-delayed" style={{ background: 'radial-gradient(circle, rgba(168,85,247,0.12) 0%, rgba(20,184,166,0.15) 40%, transparent 65%)', borderRadius: '60% 40% 30% 70% / 40% 65% 35% 60%' }} />
          <div className="absolute top-[15%] left-[25%] w-[500px] h-[500px] animate-float-slow" style={{ background: 'radial-gradient(circle, rgba(245,158,11,0.15) 0%, rgba(251,113,133,0.08) 40%, transparent 60%)', borderRadius: '50% 50% 40% 60% / 60% 35% 65% 40%' }} />
          <div className="absolute bottom-[0%] left-[50%] w-[400px] h-[400px]" style={{ background: 'radial-gradient(circle, rgba(6,182,212,0.15) 0%, rgba(16,185,129,0.08) 40%, transparent 65%)', borderRadius: '65% 35% 55% 45% / 45% 60% 40% 55%' }} />
          <div className="absolute bottom-[-10%] right-[20%] w-[500px] h-[500px] animate-float" style={{ background: 'radial-gradient(circle, rgba(244,114,182,0.12) 0%, rgba(251,191,36,0.08) 40%, transparent 60%)', borderRadius: '45% 55% 50% 50% / 55% 45% 55% 45%' }} />
        </div>

        {/* Floating sparkles */}
        <Sparkle className="absolute top-[18%] left-[12%] text-amber-400/50 animate-sparkle w-3 h-3" />
        <Sparkle className="absolute top-[22%] right-[15%] text-emerald-400/40 animate-sparkle-delayed w-4 h-4" />
        <Sparkle className="absolute top-[55%] left-[8%] text-cyan-400/35 animate-sparkle-slow w-3 h-3" />
        <Sparkle className="absolute top-[40%] right-[10%] text-pink-400/30 animate-sparkle w-2.5 h-2.5" />
        <Sparkle className="absolute top-[30%] left-[60%] text-purple-400/25 animate-sparkle-delayed w-3 h-3" />

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-white/70 backdrop-blur-md border border-emerald-200/60 text-emerald-700 text-xs font-semibold tracking-widest uppercase px-5 py-2.5 rounded-full mb-10 shadow-sm relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent animate-shimmer pointer-events-none" />
            <span className="relative flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              {isWaitlistOrBeta ? 'Private Beta \u00b7 Limited Early Access' : 'Live MLS Data \u00b7 Greater Cleveland Market'}
            </span>
          </div>

          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black leading-[1.05] tracking-tight text-stone-900 mb-7">
            Find Greater Cleveland<br />
            <span
              className="bg-clip-text text-transparent animate-shimmer-slow"
              style={{
                backgroundImage: 'linear-gradient(90deg, #047857, #0d9488, #06b6d4, #d4a04a, #ec4899, #047857)',
                backgroundSize: '200% auto',
              }}
            >
              rental deals that pay.
            </span>
          </h1>

          <p className="text-stone-600 text-base font-semibold max-w-2xl mx-auto mb-3">
            📍 Serving investors in Cuyahoga, Lake, Geauga, Lorain, Medina & Summit Counties
          </p>

          <p className="text-stone-500 text-lg sm:text-xl max-w-2xl mx-auto mb-6 leading-relaxed">
            FindBestRentals combines listing data, rent benchmarks, and estimated mortgage payments to
            instantly calculate cash flow across the Cleveland metro area. Full BRRRR analysis and
            projected appreciation help you evaluate deals in seconds.
          </p>

          <div className="max-w-3xl mx-auto mb-14">
            <details className="group">
              <summary className="text-emerald-600 hover:text-emerald-700 cursor-pointer text-sm font-semibold flex items-center justify-center gap-2 mb-2">
                <span>View all 126 cities covered</span>
                <svg className="w-4 h-4 transition-transform group-open:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </summary>
              <div className="bg-white/50 backdrop-blur-sm border border-stone-200 rounded-lg p-4 text-center">
                <div className="text-xs text-stone-600 leading-relaxed">
                  <strong className="text-stone-900">Cleveland Metro:</strong> Cleveland, Lakewood, Parma, Garfield Heights, Maple Heights, Shaker Heights, Cleveland Heights, East Cleveland, Euclid, Brooklyn •
                  <strong className="text-stone-900 ml-2">West Side:</strong> Rocky River, Westlake, Bay Village, Fairview Park, North Olmsted, Olmsted Falls, Berea, Brook Park, Strongsville, North Royalton •
                  <strong className="text-stone-900 ml-2">East Side:</strong> Beachwood, University Heights, South Euclid, Lyndhurst, Mayfield Heights, Richmond Heights, Warrensville Heights, Bedford •
                  <strong className="text-stone-900 ml-2">Lake County:</strong> Mentor, Willoughby, Eastlake, Wickliffe, Willowick, Painesville, Madison •
                  <strong className="text-stone-900 ml-2">Geauga County:</strong> Chardon, Chagrin Falls, Burton, Middlefield •
                  <strong className="text-stone-900 ml-2">Lorain County:</strong> Lorain, Elyria, Avon, Avon Lake, North Ridgeville, Oberlin, Amherst, Sheffield, Vermilion •
                  <strong className="text-stone-900 ml-2">Medina County:</strong> Medina, Wadsworth, Brunswick •
                  <strong className="text-stone-900 ml-2">Summit County (Akron):</strong> Akron, Cuyahoga Falls, Stow, Hudson, Barberton, Tallmadge, Green, Bath, Fairlawn
                </div>
              </div>
            </details>
          </div>

          {isAuthenticated ? (
            <div className="flex justify-center">
              <button
                onClick={() => navigate('/app')}
                className="group relative bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-bold text-lg px-10 py-4 rounded-xl transition-all shadow-2xl shadow-emerald-900/25 hover:-translate-y-0.5 overflow-hidden"
              >
                <span className="relative z-10">Go to App</span>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity animate-shimmer" />
              </button>
            </div>
          ) : isWaitlistOrBeta ? (
            <div id="hero-form">
              <form onSubmit={handleWaitlistSubmit} className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto mb-4">
                <GlowEmailInput />
                <button
                  type="submit"
                  disabled={loading}
                  className="group relative bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 disabled:from-emerald-300 disabled:to-teal-300 text-white font-bold px-8 py-4 rounded-xl transition-all whitespace-nowrap shadow-2xl shadow-emerald-900/25 hover:-translate-y-0.5 overflow-hidden"
                >
                  <span className="relative z-10">{loading ? 'Joining...' : 'Get Early Access'}</span>
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity animate-shimmer" />
                </button>
              </form>
              {submitError && <p className="text-red-500 text-sm mb-4">{submitError}</p>}
              <p className="text-stone-400 text-xs tracking-wide">
                No credit card &middot; No spam &middot;{' '}
                <button onClick={() => navigate('/signup')} className="text-emerald-600 hover:text-emerald-700 underline underline-offset-2 font-medium">
                  Have an invite code?
                </button>
              </p>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/signup')}
                className="group relative bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-bold text-lg px-10 py-4 rounded-xl transition-all shadow-2xl shadow-emerald-900/25 hover:-translate-y-0.5 overflow-hidden"
              >
                <span className="relative z-10">Start 30-Day Free Trial</span>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity animate-shimmer" />
              </button>
              <button
                onClick={() => navigate('/login')}
                className="bg-white/70 backdrop-blur-md border border-stone-300/50 text-stone-700 font-semibold text-lg px-10 py-4 rounded-xl hover:bg-white/90 hover:-translate-y-0.5 transition-all shadow-lg shadow-stone-900/[0.04]"
              >
                Log In
              </button>
            </div>
          )}
          {!isAuthenticated && !isWaitlistOrBeta && (
            <p className="mt-5 text-stone-400 text-sm">
              $19.99/month after trial &middot; Cancel anytime &middot; No charge for 30 days
            </p>
          )}
        </div>
      </section>

      {/* Wave transition hero -> social proof */}
      <WaveTop color="#ffffff" className="-mt-1" />

      {/* ============================================================ */}
      {/*  SOCIAL PROOF STRIP - on white                               */}
      {/* ============================================================ */}
      <section className="bg-white relative -mt-px">
        {isWaitlistOrBeta ? (
          <div className="max-w-xl mx-auto px-6 py-12 text-center">
            <p className="text-xs font-bold text-emerald-600 uppercase tracking-widest mb-5">
              Private Beta &middot; 500 spots total
            </p>
            <div className="relative h-3 bg-stone-100 rounded-full overflow-hidden mb-4 shadow-inner">
              <div
                className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-400 transition-all"
                style={{ width: `${(WAITLIST_CLAIMED / WAITLIST_TOTAL) * 100}%`, boxShadow: '0 0 16px rgba(16,185,129,0.45), 0 0 4px rgba(16,185,129,0.6)' }}
              />
            </div>
            <div className="flex justify-between items-baseline">
              <span className="text-stone-900 font-black text-2xl tabular-nums">
                {WAITLIST_CLAIMED}{' '}
                <span className="text-stone-400 text-base font-normal">/ {WAITLIST_TOTAL} spots claimed</span>
              </span>
              <span className="text-emerald-600 text-sm font-semibold tabular-nums">
                {WAITLIST_TOTAL - WAITLIST_CLAIMED} remaining
              </span>
            </div>
          </div>
        ) : (
          <div className="max-w-5xl mx-auto px-6 py-12 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: 'Any', label: 'ZIP code in the USA' },
              { value: 'Rent', label: 'Benchmarks built-in' },
              { value: '30', label: 'Days free \u2014 no card needed' },
              { value: '< 5 min', label: 'To your first deal analysis' },
            ].map(s => (
              <div key={s.label}>
                <div className="text-3xl font-black bg-gradient-to-br from-stone-900 to-stone-700 bg-clip-text text-transparent mb-1 tabular-nums">{s.value}</div>
                <div className="text-xs text-stone-500 uppercase tracking-widest leading-snug">{s.label}</div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Wave transition to vibrant section */}
      <WaveSoft color="#a7f3d0" />

      {/* ============================================================ */}
      {/*  PRODUCT SHOWCASE - "See it in action" - COMMENTED OUT     */}
      {/*  Uncomment this section to restore demo mockups             */}
      {/* ============================================================ */}
      {/* 
      <section className="relative pb-8 pt-8 px-6 overflow-hidden" style={{ background: 'linear-gradient(180deg, #a7f3d0 0%, #99f6e4 20%, #cffafe 50%, #e0e7ff 80%, #f5d0fe 100%)' }}>
        <div className="absolute top-[10%] right-[-5%] w-[500px] h-[500px] pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(6,182,212,0.15) 0%, transparent 60%)', borderRadius: '55% 45% 60% 40% / 40% 60% 40% 60%' }} />
        <div className="absolute bottom-[5%] left-[-8%] w-[400px] h-[400px] pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(168,85,247,0.10) 0%, transparent 65%)', borderRadius: '45% 55% 40% 60% / 60% 40% 55% 45%' }} />
        <div className="absolute top-[40%] left-[30%] w-[350px] h-[350px] pointer-events-none animate-float-slow" style={{ background: 'radial-gradient(circle, rgba(244,114,182,0.08) 0%, transparent 60%)', borderRadius: '40% 60% 55% 45% / 50% 50% 50% 50%' }} />

        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-emerald-700 text-xs font-bold tracking-[0.2em] uppercase mb-4">See it in action</p>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-stone-900 mb-4">
              Real tools. Real numbers. Real deals.
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {SCREENSHOTS.map((shot, i) => (
              <div key={i} className="group">
                <div className="relative rounded-2xl overflow-hidden bg-white shadow-xl shadow-stone-900/[0.10] border border-white/80 group-hover:shadow-2xl group-hover:shadow-emerald-900/[0.15] group-hover:-translate-y-2 transition-all duration-300">
                  <div className="h-2.5 w-full" style={{ background: 'linear-gradient(90deg, #059669, #0d9488, #06b6d4)' }} />
                  <shot.Mockup />
                </div>
                <div className="mt-5 text-center px-2">
                  <h3 className="text-stone-900 font-bold text-base mb-1.5">{shot.title}</h3>
                  <p className="text-stone-600 text-sm leading-relaxed">{shot.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <WaveBottom color="#f5d0fe" className="-mb-1" />
      <div style={{ background: 'linear-gradient(180deg, #ffffff 0%, #ecfdf5 100%)' }}>
        <WaveTop color="#d1fae5" className="opacity-60" />
      </div>
      */}

      {/* Wave transition to features */}

      {/* ============================================================ */}
      {/*  FEATURES - vibrant curved background                        */}
      {/* ============================================================ */}
      <section className="relative py-28 px-6 -mt-px" style={{ background: 'linear-gradient(180deg, #d1fae5 0%, #a7f3d0 20%, #99f6e4 40%, #cffafe 60%, #fef9c3 80%, #fde68a 100%)' }}>
        {/* Organic blobs */}
        <div className="absolute bottom-[10%] right-[-5%] w-[600px] h-[600px] pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(6,182,212,0.12) 0%, transparent 60%)', borderRadius: '55% 45% 35% 65% / 60% 40% 60% 40%' }} />
        <div className="absolute top-[20%] left-[-8%] w-[400px] h-[400px] pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(168,85,247,0.08) 0%, transparent 65%)', borderRadius: '40% 60% 55% 45% / 45% 55% 45% 55%' }} />
        <div className="absolute top-[50%] right-[20%] w-[300px] h-[300px] pointer-events-none animate-float" style={{ background: 'radial-gradient(circle, rgba(244,114,182,0.08) 0%, transparent 60%)', borderRadius: '50% 50% 40% 60% / 40% 60% 45% 55%' }} />

        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="text-center mb-20">
            <p className="text-emerald-700 text-xs font-bold tracking-[0.2em] uppercase mb-4">Everything you need to invest</p>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-stone-900">
              Built for serious investors.
            </h2>
            <p className="text-stone-600 mt-4 text-lg max-w-xl mx-auto leading-relaxed">
              Every number you need &mdash; before you pick up the phone.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-7">
            {FEATURES.map((f, i) => (
              <div
                key={i}
                className="group relative bg-white/80 backdrop-blur-md rounded-2xl p-8 border border-white/80 hover:-translate-y-1 hover:shadow-2xl hover:shadow-emerald-900/[0.10] transition-all duration-300 cursor-default shadow-lg shadow-stone-900/[0.04]"
              >
                <div className="absolute top-0 left-4 right-4 h-px bg-gradient-to-r from-transparent via-emerald-400/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-emerald-50/30 to-cyan-50/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                <div className="relative">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-100 to-teal-100 border border-emerald-200/50 flex items-center justify-center text-emerald-600 mb-6 shadow-sm group-hover:shadow-md group-hover:shadow-emerald-900/10 transition-shadow duration-300">
                    {f.icon}
                  </div>
                  <h3 className="text-stone-900 font-bold text-base mb-2.5">{f.title}</h3>
                  <p className="text-stone-500 text-sm leading-relaxed">{f.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Wave transition to how-it-works */}
      <WaveBottom color="#fde68a" className="-mb-px" />

      {/* ============================================================ */}
      {/*  HOW IT WORKS - white section                                */}
      {/* ============================================================ */}
      <section className="relative py-28 px-6 bg-white">
        <div className="absolute top-[20%] right-[-5%] w-[400px] h-[400px] rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(16,185,129,0.06) 0%, transparent 70%)' }} />

        <div className="max-w-4xl mx-auto relative z-10">
          <div className="text-center mb-20">
            <p className="text-emerald-600 text-xs font-bold tracking-[0.2em] uppercase mb-4">The process</p>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-stone-900">
              From ZIP code to offer in under five minutes.
            </h2>
          </div>

          <div className="relative">
            <div className="absolute left-[23px] top-2 bottom-2 w-px hidden sm:block" style={{ background: 'linear-gradient(to bottom, #10b981, rgba(6,182,212,0.5), rgba(168,85,247,0.3), rgba(245,158,11,0.2), transparent)' }} />
            <div className="space-y-8">
              {STEPS.map((s, i) => (
                <div key={i} className="flex gap-8 items-start group">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-emerald-600 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-900/20 ring-4 ring-white">
                    <span className="text-white text-xs font-black tabular-nums">{s.n}</span>
                  </div>
                  <div className="flex-1 bg-stone-50/80 backdrop-blur-sm rounded-xl p-5 border border-stone-100 group-hover:bg-white group-hover:shadow-lg group-hover:shadow-stone-900/[0.04] transition-all duration-300">
                    <h3 className="text-stone-900 font-bold text-lg mb-1.5">{s.title}</h3>
                    <p className="text-stone-500 leading-relaxed">{s.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Wave transition to testimonial */}
      <WaveSoft color="#cffafe" />

      {/* ============================================================ */}
      {/*  TESTIMONIAL - vibrant, colorful wash                        */}
      {/* ============================================================ */}
      <section className="relative py-24 px-6 overflow-hidden" style={{ background: 'linear-gradient(135deg, #cffafe 0%, #a7f3d0 25%, #fef9c3 50%, #fde68a 75%, #fbcfe8 100%)' }}>
        {/* Organic amber blob */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] pointer-events-none" style={{ background: 'radial-gradient(ellipse, rgba(168,85,247,0.10) 0%, transparent 60%)', borderRadius: '55% 45% 60% 40% / 40% 60% 40% 60%' }} />

        <div className="relative z-10 max-w-3xl mx-auto text-center">
          {/* Decorative quote */}
          <div className="mb-6 flex justify-center">
            <svg className="w-12 h-12 text-emerald-600/15" fill="currentColor" viewBox="0 0 24 24">
              <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10H14.017zM0 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151C7.546 6.068 5.983 8.789 5.983 11H10v10H0z" />
            </svg>
          </div>
          <div className="flex justify-center gap-1.5 mb-8">
            {[0, 1, 2, 3, 4].map(i => (
              <svg key={i} className="w-5 h-5 text-amber-400" fill="currentColor" viewBox="0 0 20 20" style={{ filter: 'drop-shadow(0 1px 2px rgba(245,158,11,0.3))' }}>
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            ))}
          </div>
          <blockquote className="text-xl sm:text-2xl text-stone-700 leading-relaxed mb-8 italic font-light">
            &ldquo;I used to spend two hours running numbers on a single property. Now I filter 200 listings
            in ten minutes and only call on the ones that already pencil out. It&apos;s changed how I
            invest.&rdquo;
          </blockquote>
          <div className="flex items-center justify-center gap-3">
            <div className="w-11 h-11 rounded-full bg-gradient-to-br from-emerald-600 to-teal-600 flex items-center justify-center text-white font-bold text-sm shadow-lg shadow-emerald-900/20 ring-2 ring-white/80">
              M
            </div>
            <div className="text-left">
              <div className="text-stone-900 text-sm font-semibold">Marcus T.</div>
              <div className="text-stone-400 text-xs">
                Real estate investor &middot; 12 doors &middot; Cleveland, OH
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Wave transition to pricing */}
      <WaveBottom color="#fbcfe8" className="-mb-px" />

      {/* ============================================================ */}
      {/*  PRICING (non-beta only)                                     */}
      {/* ============================================================ */}
      {!isWaitlistOrBeta && !isAuthenticated && (
        <section className="relative py-28 px-6 bg-white">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(16,185,129,0.04) 0%, transparent 70%)', borderRadius: '50% 50% 40% 60% / 55% 45% 55% 45%' }} />

          <div className="max-w-md mx-auto text-center relative z-10">
            <p className="text-emerald-600 text-xs font-bold tracking-[0.2em] uppercase mb-4">Pricing</p>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-stone-900 mb-3">Simple pricing.</h2>
            <p className="text-stone-500 mb-14 text-lg">One plan. Everything included. 30 days free.</p>

            <div className="relative rounded-3xl p-px overflow-hidden" style={{ background: 'linear-gradient(135deg, #10b981, #06b6d4, #a855f7, #f59e0b, #ec4899, #10b981)' }}>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-shimmer pointer-events-none" />
              <div className="relative bg-white/90 backdrop-blur-md rounded-3xl p-9">
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white text-xs font-bold px-5 py-1.5 rounded-full shadow-lg shadow-emerald-900/25">
                  30-DAY FREE TRIAL
                </div>
                <div className="flex items-end justify-center gap-1 mt-5 mb-1">
                  <span className="text-6xl font-black text-stone-900">$19</span>
                  <span className="text-4xl font-black text-stone-900">.99</span>
                  <span className="text-stone-400 text-lg mb-2">/mo</span>
                </div>
                <p className="text-stone-400 text-sm mb-8">Then $19.99/month. Cancel anytime.</p>
                <div className="h-px mb-8 mx-4" style={{ background: 'linear-gradient(90deg, transparent, rgba(168,85,247,0.3), rgba(16,185,129,0.2), rgba(245,158,11,0.3), transparent)' }} />
                <ul className="text-left space-y-4 mb-9">
                  {PRICING_ITEMS.map(item => (
                    <li key={item} className="flex items-start gap-3 text-sm text-stone-700">
                      <div className="w-5 h-5 rounded-full bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200/50 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <svg className="w-3 h-3 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      {item}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => navigate('/signup')}
                  className="group relative w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-bold text-lg py-4 rounded-xl transition-all shadow-xl shadow-emerald-900/20 hover:shadow-2xl hover:-translate-y-0.5 overflow-hidden"
                >
                  <span className="relative z-10">Start Free Trial</span>
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity animate-shimmer" />
                </button>
                <p className="text-stone-400 text-xs mt-4">No charge for 30 days &middot; Card required to start</p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Wave transition to CTA */}
      <WaveSoft color="#0f172a" />

      {/* ============================================================ */}
      {/*  FINAL CTA - rich dark gradient                              */}
      {/* ============================================================ */}
      <section className="relative py-28 px-6 text-white overflow-hidden -mt-px" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #0a2e29 50%, #134e4a 100%)' }}>
        {/* Glow orbs */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(16,185,129,0.12) 0%, transparent 65%)' }} />
        <div className="absolute top-0 right-[10%] w-[300px] h-[300px] pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(6,182,212,0.10) 0%, transparent 70%)', borderRadius: '50% 50% 40% 60% / 55% 45% 55% 45%' }} />

        <Sparkle className="absolute top-[20%] left-[10%] text-emerald-400/20 animate-sparkle w-3 h-3" />
        <Sparkle className="absolute top-[30%] right-[12%] text-amber-400/15 animate-sparkle-delayed w-2.5 h-2.5" />

        <div className="relative z-10 max-w-xl mx-auto text-center">
          {isWaitlistOrBeta ? (
            <>
              <p className="text-emerald-400 text-xs font-bold tracking-[0.2em] uppercase mb-4">Limited spots available</p>
              <h2 className="text-4xl sm:text-5xl font-black tracking-tight mb-5">
                Stop letting deals slip past you.
              </h2>
              <p className="text-stone-400 mb-10 leading-relaxed text-lg">
                Early members get locked-in pricing and first access when we open doors. Takes five seconds to reserve your spot.
              </p>
              {!isAuthenticated && (
                <form onSubmit={handleWaitlistSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto mb-4">
                  <GlowEmailInputDark />
                  <button
                    type="submit"
                    disabled={loading}
                    className="group relative bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 disabled:from-emerald-800 disabled:to-teal-800 text-white font-bold px-8 py-4 rounded-xl transition-all whitespace-nowrap shadow-2xl shadow-emerald-900/40 hover:-translate-y-0.5 overflow-hidden"
                  >
                    <span className="relative z-10">{loading ? 'Joining...' : 'Join the Waitlist'}</span>
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity animate-shimmer" />
                  </button>
                </form>
              )}
              {submitError && <p className="text-red-400 text-sm mb-3">{submitError}</p>}
              <p className="text-stone-500 text-xs tracking-wide">
                Already have an invite?{' '}
                <button onClick={() => navigate('/signup')} className="text-emerald-400 hover:text-emerald-300 font-semibold underline underline-offset-2">
                  Sign up here
                </button>
              </p>
            </>
          ) : (
            <>
              <p className="text-emerald-400 text-xs font-bold tracking-[0.2em] uppercase mb-4">Ready to start?</p>
              <h2 className="text-4xl sm:text-5xl font-black tracking-tight mb-5">
                {isAuthenticated ? 'Welcome back.' : 'Find your next deal today.'}
              </h2>
              <p className="text-stone-400 mb-10 leading-relaxed text-lg">
                {isAuthenticated
                  ? 'Your dashboard is ready. Jump back in and keep analyzing.'
                  : 'Join rental investors using real data to buy right. No guesswork, no spreadsheets.'}
              </p>
              <button
                onClick={() => navigate(isAuthenticated ? '/app' : '/signup')}
                className="group relative bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white font-bold text-xl px-14 py-5 rounded-2xl transition-all shadow-2xl shadow-emerald-900/40 hover:-translate-y-0.5 overflow-hidden"
              >
                <span className="relative z-10">{isAuthenticated ? 'Go to App' : 'Start Free Trial'}</span>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity animate-shimmer" />
              </button>
              {!isAuthenticated && (
                <p className="mt-5 text-stone-500 text-sm">
                  $19.99/month after trial &middot; Cancel anytime
                </p>
              )}
            </>
          )}
        </div>
      </section>

      {/* ============================================================ */}
      {/*  FOOTER                                                      */}
      {/* ============================================================ */}
      <footer className="py-10 px-6" style={{ background: 'linear-gradient(135deg, #0c1220 0%, #0f3330 100%)' }}>
        <div className="h-px max-w-6xl mx-auto mb-8" style={{ background: 'linear-gradient(90deg, transparent, rgba(168,85,247,0.2) 20%, rgba(16,185,129,0.15) 50%, rgba(245,158,11,0.2) 80%, transparent)' }} />
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="text-stone-500 text-xs">&copy; {new Date().getFullYear()} FindBestRentals. All rights reserved.</span>
          <div className="flex items-center gap-6 text-xs text-stone-500">
            <Link to="/terms-of-service" className="hover:text-stone-300 transition-colors">Terms</Link>
            <Link to="/privacy-policy" className="hover:text-stone-300 transition-colors">Privacy</Link>
            {/* Only show affiliate links if referrals are enabled and (public OR user is admin) */}
            {referralsEnabled && (
              <>
                <Link to="/affiliate-agreement" className="hover:text-stone-300 transition-colors">Affiliate Agreement</Link>
                <Link to="/affiliates" className="hover:text-stone-300 transition-colors">Partner Program</Link>
              </>
            )}
            <Link to="/login" className="hover:text-stone-300 transition-colors">Log in</Link>
          </div>
        </div>
      </footer>

    </div>
  );
}
